(() => {
  const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  const initHeader = (scope = document) => {
    const header = scope.querySelector?.('[data-bowbar-header]') || document.querySelector('[data-bowbar-header]');
    const button = document.querySelector('[data-bowbar-menu-button]');
    const drawer = document.querySelector('[data-bowbar-mobile-menu]');
    if (!header || !button || !drawer || button.dataset.bowbarReady) return;
    button.dataset.bowbarReady = 'true';
    const updateHeader = () => header.classList.toggle('is-scrolled', window.scrollY > 12);
    const close = () => {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      button.setAttribute('aria-expanded', 'false');
      button.setAttribute('aria-label', 'Open menu');
      document.body.classList.remove('bowbar-menu-open');
    };
    button.addEventListener('click', () => {
      const open = !drawer.classList.contains('is-open');
      drawer.classList.toggle('is-open', open);
      drawer.setAttribute('aria-hidden', String(!open));
      button.setAttribute('aria-expanded', String(open));
      button.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
      document.body.classList.toggle('bowbar-menu-open', open);
    });
    drawer.querySelectorAll('a').forEach((link) => link.addEventListener('click', close));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && drawer.classList.contains('is-open')) {
        close();
        button.focus();
      }
    });
    window.addEventListener('resize', () => {
      if (window.innerWidth > 980) close();
    });
    window.addEventListener('scroll', updateHeader, { passive: true });
    updateHeader();
  };

  const initReveal = (scope = document) => {
    const items = [...scope.querySelectorAll('.bowbar-reveal:not([data-bowbar-reveal-ready])')];
    if (!items.length) return;
    items.forEach((item) => { item.dataset.bowbarRevealReady = 'true'; });
    if (reducedMotion || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.18, rootMargin: '0px 0px -95px' });
    items.forEach((item) => observer.observe(item));
  };

  const initSliders = (scope = document) => {
    scope.querySelectorAll('[data-bowbar-slider]:not([data-bowbar-slider-ready])').forEach((slider) => {
      slider.dataset.bowbarSliderReady = 'true';
      const slides = [...slider.querySelectorAll('.bowbar-fade-slides img')];
      const dots = [...slider.querySelectorAll('[data-bowbar-slide-dot]')];
      if (slides.length < 2) return;
      let active = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
      let timer;
      const show = (index) => {
        active = (index + slides.length) % slides.length;
        slides.forEach((slide, slideIndex) => slide.classList.toggle('is-active', slideIndex === active));
        dots.forEach((dot, dotIndex) => {
          const selected = dotIndex === active;
          dot.classList.toggle('is-active', selected);
          if (selected) dot.setAttribute('aria-current', 'true'); else dot.removeAttribute('aria-current');
        });
      };
      const stop = () => window.clearInterval(timer);
      const start = () => {
        if (reducedMotion) return;
        stop();
        timer = window.setInterval(() => show(active + 1), 4600);
      };
      dots.forEach((dot, index) => dot.addEventListener('click', () => { show(index); start(); }));
      slider.addEventListener('mouseenter', stop);
      slider.addEventListener('mouseleave', start);
      slider.addEventListener('focusin', stop);
      slider.addEventListener('focusout', start);
      start();
    });
  };

  const initNewArrivalsSplide = (scope = document) => {
    const sliders = [...scope.querySelectorAll('[data-bowbar-new-arrivals-slider]:not([data-bowbar-splide-ready])')];
    if (!sliders.length || !window.Splide) return;
    const autoScrollExtension = window.splide?.Extensions?.AutoScroll;

    sliders.forEach((element) => {
      element.dataset.bowbarSplideReady = 'true';
      element.classList.add('is-splide-ready');

      const config = {
        perPage: 3,
        perMove: 1,
        type: 'loop',
        focus: 'center',
        gap: '2rem',
        padding: { left: '5rem', right: '5rem' },
        arrows: false,
        pagination: false,
        drag: 'free',
        snap: false,
        speed: 800,
        breakpoints: {
          980: { perPage: 2, gap: '1.25rem', padding: { left: '2.5rem', right: '2.5rem' } },
          767: { perPage: 1, gap: '1rem', padding: { left: '2rem', right: '2rem' } },
        },
      };

      if (!reducedMotion && autoScrollExtension) {
        config.autoScroll = { speed: 0.5, pauseOnHover: true, pauseOnFocus: true };
      }

      const slider = new window.Splide(element, config);
      if (!reducedMotion && autoScrollExtension) slider.mount({ AutoScroll: autoScrollExtension });
      else slider.mount();

      const revealSlides = () => {
        const slides = [...element.querySelectorAll('.splide__slide')];
        const center = window.innerWidth / 2;
        slides
          .map((slide) => {
            const rect = slide.getBoundingClientRect();
            return { slide, distance: Math.abs(rect.left + rect.width / 2 - center) };
          })
          .sort((a, b) => a.distance - b.distance)
          .forEach((item, index) => { item.slide.style.transitionDelay = `${index * 0.08}s`; });
        element.classList.add('is-visible');
      };

      if (reducedMotion || !('IntersectionObserver' in window)) {
        revealSlides();
        return;
      }

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          window.setTimeout(revealSlides, 100);
          observer.unobserve(element);
        });
      }, { threshold: 0.38, rootMargin: '0px 0px -145px 0px' });
      observer.observe(element);
    });
  };

  const shopRoot = () => {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };


  const cartState = { cart: null, opener: null, busy: false };

  const isPricingFragment = (item) => item.properties?._bowbar_role === 'price' || item.handle === 'bowbar-custom-pricing';
  const visibleCartItems = (cart) => (cart.items || []).filter((item) => !isPricingFragment(item));
  const visibleCartCount = (cart) => visibleCartItems(cart).reduce((count, item) => count + item.quantity, 0);
  const groupedCartPrice = (cart, item) => (cart.items || []).filter((entry) => entry.key === item.key || (item.properties?._bowbar_configuration && entry.properties?._bowbar_configuration === item.properties._bowbar_configuration)).reduce((sum, entry) => sum + Number(entry.final_line_price ?? entry.line_price), 0);

  // Match CRBN's exact-cent merchandise decomposition; never round to a nearby charge.
  const pricingFragments = (cents, fragments) => {
    if (!Number.isSafeInteger(cents) || cents < 1 || cents > 500000) return null;
    const available = new Map(fragments.filter((part) => part.available && Number.isSafeInteger(part.cents) && part.cents > 0).map((part) => [part.cents, part]));
    const parts = [];
    let remaining = cents;
    for (const amount of [...available.keys()].sort((a, b) => b - a)) {
      while (remaining >= amount) {
        parts.push(available.get(amount));
        remaining -= amount;
      }
    }
    return remaining === 0 && parts.length <= 12 ? parts : null;
  };

  const formatCartMoney = (cents, currency = 'USD') => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(Number(cents || 0) / 100);
    } catch (error) {
      return `$${(Number(cents || 0) / 100).toFixed(2)}`;
    }
  };

  const createCartNode = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = String(text);
    return node;
  };

  const setCartCount = (count) => {
    const quantity = Math.max(0, Number(count || 0));
    document.querySelectorAll('[data-bowbar-cart-count]').forEach((node) => {
      node.textContent = String(quantity);
      node.hidden = quantity === 0;
    });
    document.querySelectorAll('[data-bowbar-cart-toggle]').forEach((node) => {
      node.setAttribute('aria-label', `Cart with ${quantity} ${quantity === 1 ? 'item' : 'items'}`);
    });
  };

  const dispatchCartUpdate = (cart, source = 'bowbar-cart') => {
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: cart,
        sourceId: 'bowbar-cart',
        data: { source, itemCount: Number(cart?.item_count || 0) },
      },
    }));
  };

  const renderCart = (cart) => {
    const drawer = document.querySelector('[data-bowbar-cart-drawer]');
    const itemsRoot = drawer?.querySelector('[data-bowbar-cart-items]');
    const footer = drawer?.querySelector('[data-bowbar-cart-footer]');
    const subtotal = drawer?.querySelector('[data-bowbar-cart-subtotal]');
    if (!drawer || !itemsRoot || !footer || !subtotal) return;

    cartState.cart = cart;
    setCartCount(visibleCartCount(cart));
    itemsRoot.replaceChildren();
    subtotal.textContent = formatCartMoney(cart.total_price, cart.currency || 'USD');
    footer.hidden = cart.item_count === 0;

    if (!cart.items?.length) {
      const empty = createCartNode('div', 'bowbar-cart-drawer__empty');
      empty.append(createCartNode('h3', '', 'Your cart is empty'), createCartNode('p', '', 'Add something special and it will appear here without leaving the page.'));
      itemsRoot.append(empty);
      return;
    }

    visibleCartItems(cart).forEach((item) => {
      const article = createCartNode('article', 'bowbar-cart-item');
      if (item.image) {
        const media = createCartNode('a', 'bowbar-cart-item__media');
        media.href = item.url || '/cart';
        const image = document.createElement('img');
        image.src = item.image;
        image.alt = item.product_title || item.title || '';
        image.loading = 'lazy';
        media.append(image);
        article.append(media);
      }

      const details = createCartNode('div', 'bowbar-cart-item__details');
      const title = createCartNode('a', 'bowbar-cart-item__title', item.product_title || item.title);
      title.href = item.url || '/cart';
      details.append(title);
      if (!item.properties?._bowbar_configuration && item.variant_title && item.variant_title !== 'Default Title') details.append(createCartNode('p', 'bowbar-cart-item__variant', item.variant_title));

      const properties = Object.entries(item.properties || {}).filter(([key, value]) => key && !key.startsWith('_') && key !== 'Configured unit price' && value !== null && value !== undefined && String(value).trim() !== '');
      if (properties.length) {
        const propertyList = createCartNode('dl', 'bowbar-cart-item__properties');
        properties.forEach(([key, value]) => {
          propertyList.append(createCartNode('dt', '', key), createCartNode('dd', '', /^https?:\/\//i.test(String(value)) ? 'Uploaded file' : String(value)));
        });
        details.append(propertyList);
      }

      details.append(createCartNode('strong', 'bowbar-cart-item__price', formatCartMoney(groupedCartPrice(cart, item), cart.currency || 'USD')));

      const actions = createCartNode('div', 'bowbar-cart-item__actions');
      const quantity = createCartNode('div', 'bowbar-cart-item__quantity');
      const decrease = createCartNode('button', '', '−');
      decrease.type = 'button';
      decrease.dataset.bowbarCartChange = '-1';
      decrease.dataset.bowbarCartKey = item.key;
      decrease.setAttribute('aria-label', `Decrease ${item.product_title || item.title} quantity`);
      const quantityText = createCartNode('span', '', item.quantity);
      quantityText.setAttribute('aria-label', `Quantity ${item.quantity}`);
      const increase = createCartNode('button', '', '+');
      increase.type = 'button';
      increase.dataset.bowbarCartChange = '1';
      increase.dataset.bowbarCartKey = item.key;
      increase.setAttribute('aria-label', `Increase ${item.product_title || item.title} quantity`);
      quantity.append(decrease, quantityText, increase);

      const remove = createCartNode('button', 'bowbar-cart-item__remove', 'Remove');
      remove.type = 'button';
      remove.dataset.bowbarCartRemove = 'true';
      remove.dataset.bowbarCartKey = item.key;
      actions.append(quantity, remove);
      details.append(actions);
      article.append(details);
      itemsRoot.append(article);
    });
  };

  const fetchCart = async () => {
    const response = await fetch(`${shopRoot()}cart.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
    if (!response.ok) throw new Error('Unable to load your cart.');
    return response.json();
  };

  const refreshCart = async () => {
    const cart = await fetchCart();
    renderCart(cart);
    return cart;
  };

  const closeCart = () => {
    const drawer = document.querySelector('[data-bowbar-cart-drawer]');
    const overlay = document.querySelector('[data-bowbar-cart-overlay]');
    if (!drawer || !overlay) return;
    drawer.classList.remove('is-open');
    overlay.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    overlay.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('bowbar-cart-open');
    const opener = cartState.opener;
    cartState.opener = null;
    if (opener instanceof HTMLElement && document.contains(opener)) opener.focus({ preventScroll: true });
  };

  const openCart = async (opener, cart = null) => {
    const drawer = document.querySelector('[data-bowbar-cart-drawer]');
    const overlay = document.querySelector('[data-bowbar-cart-overlay]');
    const closeButton = drawer?.querySelector('[data-bowbar-cart-close]');
    const itemsRoot = drawer?.querySelector('[data-bowbar-cart-items]');
    if (!drawer || !overlay) return;
    cartState.opener = opener instanceof HTMLElement ? opener : document.activeElement;
    drawer.classList.add('is-open');
    overlay.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    overlay.setAttribute('aria-hidden', 'false');
    document.body.classList.add('bowbar-cart-open');
    if (cart) renderCart(cart);
    else {
      if (itemsRoot) itemsRoot.replaceChildren(createCartNode('p', 'bowbar-cart-drawer__loading', 'Loading your cart…'));
      try { await refreshCart(); } catch (error) {
        if (itemsRoot) itemsRoot.replaceChildren(createCartNode('p', 'bowbar-cart-drawer__error', error.message || 'Unable to load your cart.'));
      }
    }
    closeButton?.focus({ preventScroll: true });
  };

  const updateCartLine = async (key, quantity) => {
    if (cartState.busy) return;
    const drawer = document.querySelector('[data-bowbar-cart-drawer]');
    cartState.busy = true;
    drawer?.classList.add('is-loading');
    try {
      const currentCart = await fetchCart();
      const item = currentCart.items.find((entry) => entry.key === key);
      const group = item?.properties?._bowbar_configuration;
      const updates = Object.fromEntries(currentCart.items.filter((entry) => entry.key === key || (group && entry.properties?._bowbar_configuration === group)).map((entry) => [entry.key, Math.max(0, Number(quantity || 0))]));
      const response = await fetch(`${shopRoot()}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ updates }),
      });
      const cart = await response.json();
      if (!response.ok || cart.status) throw new Error(cart.description || cart.message || 'Unable to update your cart.');
      // Read the canonical cart after nested-line cascades and checkout-session
      // recalculations finish, instead of displaying an intermediate update result.
      const updatedCart = await fetchCart();
      renderCart(updatedCart);
      dispatchCartUpdate(updatedCart, 'bowbar-cart-drawer');
      if (window.location.pathname.replace(/\/$/, '').endsWith('/cart')) window.location.reload();
    } catch (error) {
      const itemsRoot = window.location.pathname.replace(/\/$/, '').endsWith('/cart') ? document.querySelector('.cart-page__items') : drawer?.querySelector('[data-bowbar-cart-items]');
      if (itemsRoot) itemsRoot.prepend(createCartNode('p', 'bowbar-cart-drawer__error', error.message || 'Unable to update your cart.'));
    } finally {
      cartState.busy = false;
      drawer?.classList.remove('is-loading');
    }
  };

  const initCartDrawer = (scope = document) => {
    const drawer = scope.querySelector?.('[data-bowbar-cart-drawer]') || document.querySelector('[data-bowbar-cart-drawer]');
    const overlay = document.querySelector('[data-bowbar-cart-overlay]');
    if (!drawer || !overlay || drawer.dataset.bowbarCartReady) return;
    drawer.dataset.bowbarCartReady = 'true';

    document.querySelectorAll('[data-bowbar-cart-toggle]').forEach((toggle) => {
      if (toggle.dataset.bowbarCartToggleReady) return;
      toggle.dataset.bowbarCartToggleReady = 'true';
      toggle.addEventListener('click', (event) => {
        event.preventDefault();
        openCart(toggle);
      });
    });

    drawer.querySelector('[data-bowbar-cart-close]')?.addEventListener('click', closeCart);
    overlay.addEventListener('click', closeCart);
    drawer.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const control = target?.closest('[data-bowbar-cart-change],[data-bowbar-cart-remove]');
      if (!control || !cartState.cart) return;
      const key = control.dataset.bowbarCartKey;
      const item = cartState.cart.items?.find((entry) => entry.key === key);
      if (!key || !item) return;
      const quantity = control.dataset.bowbarCartRemove === 'true' ? 0 : item.quantity + Number(control.dataset.bowbarCartChange || 0);
      updateCartLine(key, quantity);
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && drawer.classList.contains('is-open')) closeCart();
    });
    document.addEventListener('cart:update', (event) => {
      const source = String(event.detail?.data?.source || '');
      if (source.startsWith('bowbar-')) return;
      refreshCart().catch(() => {});
    });
  };

  const initItemWaiver = (form, submit) => {
    const content = form.querySelector('[data-bowbar-item-waiver]');
    const dialog = form.querySelector('[data-bowbar-waiver-dialog]');
    const checkbox = content?.querySelector('[data-bowbar-waiver-checkbox]');
    if (!content || !dialog || !checkbox || typeof dialog.showModal !== 'function') return async () => true;
    document.getElementById(dialog.id)?.remove();
    document.body.appendChild(dialog);
    dialog.querySelector('[data-bowbar-waiver-content]').appendChild(content);
    content.querySelector('h2').hidden = true;
    // Native form association preserves the consent and terms in the cart payload.
    // Without JavaScript, the original inline required checkbox remains usable.
    checkbox.required = false;
    const accept = dialog.querySelector('[data-bowbar-waiver-accept]');
    checkbox.addEventListener('change', () => { accept.disabled = !checkbox.checked; });
    accept.addEventListener('click', () => {
      if (checkbox.checked) dialog.close('accepted');
    });
    dialog.querySelectorAll('[data-bowbar-waiver-cancel]').forEach((button) => {
      button.addEventListener('click', () => dialog.close('cancelled'));
    });
    form.addEventListener('reset', () => { checkbox.checked = false; accept.disabled = true; });
    return () => new Promise((resolve) => {
      checkbox.checked = false;
      accept.disabled = true;
      dialog.returnValue = '';
      const scrollY = window.scrollY;
      document.body.style.setProperty('--bowbar-chart-scroll-top', `-${scrollY}px`);
      document.documentElement.classList.add('bowbar-chart-open');
      document.body.classList.add('bowbar-chart-open');
      dialog.addEventListener('close', () => {
        const accepted = dialog.returnValue === 'accepted' && checkbox.checked;
        if (!accepted) checkbox.checked = false;
        document.documentElement.classList.remove('bowbar-chart-open');
        document.body.classList.remove('bowbar-chart-open');
        document.body.style.removeProperty('--bowbar-chart-scroll-top');
        window.scrollTo({ top: scrollY, behavior: 'instant' });
        submit.focus({ preventScroll: true });
        resolve(accepted);
      }, { once: true });
      dialog.showModal();
      dialog.querySelector('[data-bowbar-waiver-content]').scrollTop = 0;
    });
  };

  const initAjaxProductForms = (scope = document) => {
    scope.querySelectorAll('form.bowbar-product-form:not([data-bowbar-ajax-ready])').forEach((form) => {
      form.dataset.bowbarAjaxReady = 'true';
      const submit = form.querySelector('[data-bowbar-add-to-cart]');
      const submitLabel = form.querySelector('[data-bowbar-add-label]');
      const status = form.querySelector('[data-bowbar-cart-status]');
      if (!submit) return;
      const requestItemWaiver = initItemWaiver(form, submit);

      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (submit.disabled || form.dataset.bowbarCartBusy === 'true' || form.dataset.bowbarWaiverPending === 'true') return;
        if (!form.checkValidity()) {
          form.reportValidity();
          return;
        }
        form.dataset.bowbarWaiverPending = 'true';
        const waiverAccepted = await requestItemWaiver();
        delete form.dataset.bowbarWaiverPending;
        if (!waiverAccepted || submit.disabled) return;

        const idInput = form.querySelector('input[name="id"]');
        const submittedVariantId = idInput?.value || '';
        const originalLabel = submitLabel?.textContent || 'Add to cart';
        form.dataset.bowbarCartBusy = 'true';
        submit.disabled = true;
        if (submitLabel) submitLabel.textContent = 'Adding…';
        if (status) status.textContent = 'Adding to your cart…';

        try {
          const formData = form.bowbarPricing ? form.bowbarPricing.build() : new FormData(form);
          const response = await fetch(`${shopRoot()}cart/add.js`, {
            method: 'POST',
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
            body: formData,
          });
          const addedItem = await response.json();
          if (!response.ok || addedItem.status) throw new Error(addedItem.description || addedItem.message || 'Unable to add this item to your cart.');

          const cart = await refreshCart();
          dispatchCartUpdate(cart, 'bowbar-product-form');
          if (submitLabel) submitLabel.textContent = 'Added to cart';
          if (status) status.textContent = 'Added to cart. You can keep shopping or review your cart.';
          await openCart(submit, cart);

          window.setTimeout(() => {
            if (idInput?.value === submittedVariantId && submitLabel?.textContent === 'Added to cart') submitLabel.textContent = originalLabel;
          }, 1800);
        } catch (error) {
          if (submitLabel) submitLabel.textContent = originalLabel;
          if (status) status.textContent = error.message || 'Unable to add this item to your cart.';
        } finally {
          form.dataset.bowbarCartBusy = 'false';
          if (!idInput || idInput.value === submittedVariantId) submit.disabled = false;
        }
      });
    });
  };

  const initConfiguredPricing = (section) => {
    const node = section.querySelector('[data-bowbar-configured-pricing]');
    if (!node) return false;
    const form = section.querySelector('form.bowbar-product-form');
    const submit = form.querySelector('[data-bowbar-add-to-cart]');
    const price = section.querySelector('[data-bowbar-product-price]');
    const compare = section.querySelector('[data-bowbar-product-compare]');
    const status = section.querySelector('[data-bowbar-product-status]');
    let config;
    let catalog;
    try {
      config = JSON.parse(node.textContent);
      catalog = JSON.parse(section.querySelector('[data-bowbar-pricing-catalog]').textContent);
    } catch (error) {
      status.textContent = 'Pricing could not load. Please refresh or request a quote.';
      return true;
    }
    if (compare) compare.hidden = true;
    const controls = Object.fromEntries([...section.querySelectorAll('[data-bowbar-pricing-step]')].map((input) => [input.dataset.bowbarPricingStep, input]));
    const adultOnly = ['adult-custom-embroidery-applique', 'mama-grandma-family-embroidery', 'custom-professional-business-embroidery'].includes(config.handle);
    const kidsOnly = ['baby-kids-custom-embroidery-applique', 'custom-baby-kids-embroidery'].includes(config.handle);
    catalog = catalog.filter((style) => (!adultOnly || style.age === 'Adult') && (!kidsOnly || style.age !== 'Adult') && (config.handle !== 'custom-embroidered-sweatshirt-crewneck' || style.type === 'crewneck sweatshirt') && (config.handle !== 'custom-embroidered-t-shirt' || style.type === 'T-shirt'));
    let selected;
    let total;
    let parts;
    let initialAge = config.defaultAge;
    const choiceLabel = (key, value) => {
      const labels = { 'long-sleeve tee': 'Long-sleeve T-shirt', 'long sleeve': 'Long-sleeve T-shirt', 'crewneck sweatshirt': 'Crewneck sweatshirt', hoodie: 'Hoodie', bodysuit: 'Bodysuit', 'long-sleeve bodysuit': 'Long-sleeve bodysuit', embroidery: 'Embroidery', applique: 'Appliqué' };
      return key === 'type' || key === 'technique' ? labels[value] || value : value;
    };
    const resolveChoices = (values) => {
      const resolved = { ...values };
      let matches = catalog;
      for (const key of ['age', 'brand', 'type']) {
        const available = [...new Set(matches.map((style) => style[key]))];
        resolved[key] = available.includes(resolved[key]) ? resolved[key] : available[0];
        matches = matches.filter((style) => style[key] === resolved[key]);
      }
      const style = matches[0];
      resolved.size = style?.sizes.includes(resolved.size) ? resolved.size : style?.sizes[0];
      const charges = style?.brand === 'Comfort Colors' && resolved.technique === 'applique' ? config.ccAppliqueCharges : config.sizeCharges;
      const extra = style?.age === 'Adult' ? charges[resolved.size] || 0 : 0;
      return { style, values: resolved, amount: Number(style?.[resolved.technique]) + Number(extra) };
    };
    const updateChoicePrices = () => {
      const current = Object.fromEntries(Object.entries(controls).map(([key, input]) => [key, input.value]));
      for (const [key, input] of Object.entries(controls)) {
        for (const option of input.options) {
          const candidate = resolveChoices({ ...current, [key]: option.value });
          const available = config.anchorPrice === 0 && config.anchorAvailable && pricingFragments(candidate.amount, config.fragments);
          const label = choiceLabel(key, option.value);
          const difference = candidate.amount - total;
          option.textContent = option.selected ? label : !available ? `${label} (request a quote)` : !Number.isFinite(total) || total <= 0 ? `${label} (${formatCartMoney(candidate.amount, 'USD')})` : difference === 0 ? `${label} (same price)` : `${label} (${difference > 0 ? '+' : '-'}${formatCartMoney(Math.abs(difference), 'USD')})`;
        }
      }
    };
    const fill = (input, values) => {
      const previous = input.value;
      input.replaceChildren(...values.map((value) => new Option(choiceLabel(input.dataset.bowbarPricingStep, value), value)));
      input.value = values.includes(previous) ? previous : (values[0] || '');
    };
    const refresh = () => {
      if (!config.ownItem) {
        let matches = catalog;
        for (const key of ['age', 'brand', 'type']) {
          fill(controls[key], [...new Set(matches.map((style) => style[key]))]);
          if (key === 'age' && initialAge) {
            if ([...controls.age.options].some((option) => option.value === initialAge)) controls.age.value = initialAge;
            initialAge = null;
          }
          matches = matches.filter((style) => style[key] === controls[key].value);
        }
        selected = matches[0];
        fill(controls.size, selected?.sizes || []);
        total = resolveChoices(Object.fromEntries(Object.entries(controls).map(([key, input]) => [key, input.value]))).amount;
        updateChoicePrices();
      } else total = Number(config.ownItemPrice);
      parts = config.anchorPrice === 0 && config.anchorAvailable ? pricingFragments(total, config.fragments) : null;
      const valid = Boolean(parts && config.anchorId && submit.dataset.bowbarOrderBlocked !== 'true');
      price.textContent = Number.isFinite(total) && total > 0 ? formatCartMoney(total, 'USD') : 'Request a quote';
      status.textContent = valid ? 'Made just for you. Review your selections before adding to cart.' : 'This configuration is unavailable. Please request a quote.';
      submit.disabled = !valid || form.dataset.bowbarCartBusy === 'true';
      const label = submit.querySelector('[data-bowbar-add-label]');
      if (label && form.dataset.bowbarCartBusy !== 'true') label.textContent = valid ? 'Add to cart' : 'Unavailable';
    };
    Object.values(controls).forEach((input) => input.addEventListener('change', refresh));
    form.bowbarPricing = { build: () => {
      refresh();
      if (!parts || submit.dataset.bowbarOrderBlocked === 'true') throw new Error('This configuration cannot be priced. Please request a quote.');
      const quantity = Number(form.querySelector('[name="quantity"]')?.value || 1);
      if (!Number.isInteger(quantity) || quantity < 1 || quantity > 99) throw new Error('Please choose a quantity from 1 to 99, or contact us for a bulk quote.');
      const data = new FormData();
      const group = window.crypto?.randomUUID?.() || `bowbar-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      for (const [key, value] of new FormData(form)) {
        if (!key.startsWith('properties[') || (value instanceof File && value.size === 0)) continue;
        data.append(`items[0][properties][${key.slice(11, -1)}]`, value);
      }
      data.set('items[0][id]', String(config.anchorId));
      data.set('items[0][quantity]', String(quantity));
      data.set('items[0][properties][_bowbar_configuration]', group);
      data.set('items[0][properties][_bowbar_role]', 'design');
      data.set('items[0][properties][_bowbar_unit_cents]', String(total));
      data.set('items[0][properties][_bowbar_fragment_count]', String(parts.length));
      data.set('items[0][properties][Configured unit price]', formatCartMoney(total, 'USD'));
      if (!config.ownItem) {
        data.set('items[0][properties][Technique]', choiceLabel('technique', controls.technique.value));
        data.set('items[0][properties][Garment type]', choiceLabel('type', controls.type.value));
      }
      if (config.ownItem) data.set('items[0][properties][Service]', 'One name or monogram - one placement');
      const description = config.ownItem ? 'Customer-supplied item personalization' : [selected.age, selected.brand, selected.type, controls.size.value, choiceLabel('technique', controls.technique.value)].join(' / ');
      // Indexed FormData preserves reference-image File uploads on the parent line.
      parts.forEach((part, index) => {
        const prefix = `items[${index + 1}]`;
        data.set(`${prefix}[id]`, String(part.id));
        data.set(`${prefix}[quantity]`, String(quantity));
        data.set(`${prefix}[parent_id]`, String(config.anchorId));
        data.set(`${prefix}[properties][_bowbar_configuration]`, group);
        data.set(`${prefix}[properties][_bowbar_role]`, 'price');
        data.set(`${prefix}[properties][_bowbar_fragment]`, String(index + 1));
        data.set(`${prefix}[properties][_bowbar_fragment_cents]`, String(part.cents));
        data.set(`${prefix}[properties][Configuration]`, description);
      });
      return data;
    }};
    refresh();
    return true;
  };

  const initProduct = (scope = document) => {
    scope.querySelectorAll('[data-bowbar-product]:not([data-bowbar-product-ready])').forEach((section) => {
      section.dataset.bowbarProductReady = 'true';
      const media = [...section.querySelectorAll('[data-bowbar-media-id]')];
      const mediaButtons = [...section.querySelectorAll('[data-bowbar-media-button]')];
      const thumbTrack = section.querySelector('[data-bowbar-thumb-track]');
      const thumbPrev = section.querySelector('[data-bowbar-thumb-prev]');
      const thumbNext = section.querySelector('[data-bowbar-thumb-next]');
      const updateThumbArrows = () => {
        if (!thumbTrack || !thumbPrev || !thumbNext) return;
        const maxScroll = Math.max(0, thumbTrack.scrollWidth - thumbTrack.clientWidth);
        thumbPrev.disabled = thumbTrack.scrollLeft <= 2;
        thumbNext.disabled = thumbTrack.scrollLeft >= maxScroll - 2;
      };
      const moveThumbSlider = (direction) => {
        if (!thumbTrack) return;
        const firstThumb = mediaButtons[0];
        const gap = Number.parseFloat(window.getComputedStyle(thumbTrack).columnGap || window.getComputedStyle(thumbTrack).gap || '0') || 0;
        const step = firstThumb ? firstThumb.getBoundingClientRect().width + gap : thumbTrack.clientWidth * .8;
        const visibleSteps = Math.max(1, Math.floor(thumbTrack.clientWidth / Math.max(step, 1)) - 1);
        thumbTrack.scrollBy({ left: direction * step * visibleSteps, behavior: reducedMotion ? 'auto' : 'smooth' });
      };
      if (thumbPrev && thumbNext && thumbTrack) {
        thumbPrev.addEventListener('click', () => moveThumbSlider(-1));
        thumbNext.addEventListener('click', () => moveThumbSlider(1));
        thumbTrack.addEventListener('scroll', updateThumbArrows, { passive: true });
        window.addEventListener('resize', updateThumbArrows, { passive: true });
        window.requestAnimationFrame(updateThumbArrows);
      }
      const showMedia = (id) => {
        media.forEach((item) => { item.hidden = item.dataset.bowbarMediaId !== String(id); });
        mediaButtons.forEach((button) => {
          const selected = button.dataset.bowbarMediaButton === String(id);
          button.classList.toggle('is-active', selected);
          if (selected) {
            button.setAttribute('aria-current', 'true');
            button.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
          } else {
            button.removeAttribute('aria-current');
          }
        });
      };
      mediaButtons.forEach((button) => button.addEventListener('click', () => showMedia(button.dataset.bowbarMediaButton)));

      section.querySelectorAll('[data-bowbar-quantity-change]').forEach((button) => {
        button.addEventListener('click', () => {
          const input = button.parentElement.querySelector('input[name="quantity"]');
          const minimum = Number(input.min || 1);
          input.value = String(Math.max(minimum, Number(input.value || minimum) + Number(button.dataset.bowbarQuantityChange)));
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });

      if (initConfiguredPricing(section)) return;
      const variantsNode = section.querySelector('[data-bowbar-variants]');
      const designInput = section.querySelector('[data-bowbar-design-request]');
      const designRequest = new URL(window.location.href).searchParams.get('design');
      if (designInput && designRequest) designInput.value = designRequest.slice(0, 160);
      const configNode = section.querySelector('[data-bowbar-product-config]');
      const optionInputs = [...section.querySelectorAll('[data-bowbar-option-index]')];
      const optionChoices = optionInputs.map((input) => [...input.options].map((option) => ({ value: option.value, label: option.textContent })));
      if (!variantsNode || !configNode || !optionInputs.length) return;
      let variants = [];
      let config = {};
      try {
        variants = JSON.parse(variantsNode.textContent);
        config = JSON.parse(configNode.textContent);
      } catch (error) {
        return;
      }
      const idInput = section.querySelector('[data-bowbar-variant-id]');
      const price = section.querySelector('[data-bowbar-product-price]');
      const compare = section.querySelector('[data-bowbar-product-compare]');
      const status = section.querySelector('[data-bowbar-product-status]');
      const submit = section.querySelector('[data-bowbar-add-to-cart]');
      const submitLabel = section.querySelector('[data-bowbar-add-label]');
      const formatMoney = (cents) => {
        try { return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: config.currency }).format(cents / 100); }
        catch (error) { return `$${(cents / 100).toFixed(2)}`; }
      };
      const garmentInput = section.querySelector('[data-bowbar-garment-picker]');
      const garmentIndex = optionInputs.indexOf(garmentInput);
      if (garmentInput && garmentIndex >= 0) {
        // These are shopping steps, not extra Shopify options. Keep the existing
        // Garment value and variant ID as the source of checkout pricing.
        const garments = optionChoices[garmentIndex].map(({ value }) => {
          let name = value;
          let age = 'Adult';
          const agePrefix = name.match(/^(Adult|Baby|Toddler|Youth) - /);
          if (agePrefix) {
            age = agePrefix[1];
            name = name.slice(agePrefix[0].length);
          } else if (name.startsWith('Youth ')) {
            age = 'Youth';
            name = name.slice(6);
          } else if (/bodysuit/i.test(name)) age = 'Baby';
          else if (/toddler/i.test(name)) age = 'Toddler';
          else if (name.startsWith('Rabbit Skins ')) age = 'Baby';
          const brand = ['Comfort Colors', 'Rabbit Skins', 'Gildan', 'Jerzees'].find((item) => name.startsWith(`${item} `));
          if (!brand) return null;
          const style = name.slice(brand.length + 1).replace(/^toddler /i, '');
          const types = { 'T-shirt': 'T-shirt', 'long-sleeve tee': 'Long-sleeve T-shirt', 'long sleeve': 'Long-sleeve T-shirt', 'crewneck sweatshirt': 'Crewneck sweatshirt', hoodie: 'Hoodie', bodysuit: 'Bodysuit', 'long-sleeve bodysuit': 'Long-sleeve bodysuit' };
          const type = types[style];
          return type ? { value, age, brand, type } : null;
        });
        // If the merchant introduces an unfamiliar garment name, retain the
        // normal native selector rather than guess a brand/style mapping.
        if (garments.length && garments.every(Boolean)) {
          const originalLabel = garmentInput.closest('label');
          const current = garments.find((item) => item.value === garmentInput.value) || garments[0];
          const steps = [['age', 'Age group'], ['brand', 'Brand'], ['type', 'Garment type']].map(([key, text]) => {
            const label = document.createElement('label');
            const select = document.createElement('select');
            select.id = `${garmentInput.id}-${key}`;
            select.dataset.bowbarGarmentStep = key;
            select.value = current[key];
            label.htmlFor = select.id;
            label.append(document.createTextNode(text), select);
            originalLabel.before(label);
            return { key, select, initialValue: current[key] };
          });
          const updateGarmentSteps = (initial = false) => {
            let matching = garments;
            steps.forEach(({ key, select, initialValue }) => {
              const previous = initial ? initialValue : select.value;
              const values = [...new Set(matching.map((item) => item[key]))];
              select.replaceChildren(...values.map((value) => new Option(value, value)));
              select.value = values.includes(previous) ? previous : values[0];
              matching = matching.filter((item) => item[key] === select.value);
            });
            garmentInput.value = matching[0].value;
          };
          updateGarmentSteps(true);
          originalLabel.hidden = true;
          steps.forEach(({ select }) => select.addEventListener('change', () => {
            updateGarmentSteps();
            updateVariant();
          }));
        }
      }
      const updateVariant = () => {
        // Limit each dependent dropdown to combinations belonging to the selected
        // garment. This keeps baby sizes out of adult/youth garment selections.
        optionInputs.forEach((input, index) => {
          const prefix = optionInputs.slice(0, index).map((control) => control.value);
          const selectedValue = input.value;
          const choices = optionChoices[index].filter((option) => variants.some((item) => item.options[index] === option.value && prefix.every((value, at) => item.options[at] === value)));
          input.replaceChildren(...choices.map((choice) => new Option(choice.label, choice.value)));
          input.value = choices.some((choice) => choice.value === selectedValue) ? selectedValue : (choices[0]?.value || '');
        });
        const selected = optionInputs.map((input) => input.value);
        const variant = variants.find((item) => item.options.every((value, index) => value === selected[index]));
        if (!variant) {
          if (submit) submit.disabled = true;
          if (submitLabel) submitLabel.textContent = config.soldOutLabel;
          if (status) status.textContent = config.soldOutLabel;
          return;
        }
        if (idInput) idInput.value = variant.id;
        if (price) price.textContent = formatMoney(variant.price);
        if (compare) {
          const onSale = Number(variant.compare_at_price) > Number(variant.price);
          compare.hidden = !onSale;
          compare.textContent = onSale ? formatMoney(variant.compare_at_price) : '';
        }
        if (submit) submit.disabled = !variant.available || submit.dataset.bowbarOrderBlocked === 'true';
        if (submitLabel) submitLabel.textContent = variant.available ? config.addLabel : config.soldOutLabel;
        if (status) status.textContent = variant.available ? config.availableText : config.soldOutLabel;
        if (variant.featured_media?.id) showMedia(variant.featured_media.id);
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      };
      optionInputs.forEach((input) => input.addEventListener('change', updateVariant));
      updateVariant();
    });
  };

  const initChartGuides = (scope) => {
    scope.querySelectorAll('[data-bowbar-chart-guide]').forEach((guide) => {
      if (guide.dataset.chartReady) return;
      guide.dataset.chartReady = 'true';
      const opener = guide.querySelector('[data-bowbar-chart-open]');
      const dialog = guide.querySelector('[data-bowbar-chart-dialog]');
      if (!opener || !dialog) return;
      // Keep the modal outside the product form and transformed page sections.
      document.getElementById(dialog.id)?.remove();
      document.body.appendChild(dialog);
      const title = dialog.querySelector('[data-bowbar-chart-title]');
      const directory = dialog.querySelector('[data-bowbar-chart-directory]');
      const viewer = dialog.querySelector('[data-bowbar-chart-viewer]');
      const image = dialog.querySelector('[data-bowbar-chart-image]');
      const stage = dialog.querySelector('[data-bowbar-chart-stage]');
      const status = dialog.querySelector('[data-bowbar-chart-status]');
      const zoom = dialog.querySelector('[data-bowbar-chart-zoom]');
      const back = dialog.querySelector('[data-bowbar-chart-back]');
      let selectedLink;
      let pageScrollY = 0;
      const resetZoom = () => {
        stage.classList.remove('is-zoomed');
        zoom.textContent = 'Zoom in';
        zoom.setAttribute('aria-pressed', 'false');
        stage.scrollTop = 0;
        stage.scrollLeft = 0;
      };
      const showDirectory = () => {
        directory.hidden = false;
        viewer.hidden = true;
        title.textContent = 'Color, size & font guide';
        resetZoom();
      };
      opener.addEventListener('click', () => {
        showDirectory();
        pageScrollY = window.scrollY;
        document.body.style.setProperty('--bowbar-chart-scroll-top', `-${pageScrollY}px`);
        document.documentElement.classList.add('bowbar-chart-open');
        document.body.classList.add('bowbar-chart-open');
        dialog.showModal();
      });
      dialog.querySelector('[data-bowbar-chart-close]').addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', (event) => {
        if (event.target !== dialog) return;
        const bounds = dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close();
      });
      dialog.addEventListener('close', () => {
        document.documentElement.classList.remove('bowbar-chart-open');
        document.body.classList.remove('bowbar-chart-open');
        document.body.style.removeProperty('--bowbar-chart-scroll-top');
        window.scrollTo({ top: pageScrollY, behavior: 'instant' });
        image.removeAttribute('src');
        opener.focus({ preventScroll: true });
      });
      dialog.querySelectorAll('[data-bowbar-chart-link]').forEach((link) => {
        link.addEventListener('click', (event) => {
          event.preventDefault();
          selectedLink = link;
          directory.hidden = true;
          viewer.hidden = false;
          title.textContent = link.dataset.chartTitle;
          image.alt = link.dataset.chartTitle;
          image.hidden = true;
          status.textContent = 'Loading chart...';
          resetZoom();
          image.src = link.href;
          back.focus({ preventScroll: true });
        });
      });
      image.addEventListener('load', () => {
        image.width = image.naturalWidth;
        image.height = image.naturalHeight;
        image.hidden = false;
        status.textContent = '';
      });
      image.addEventListener('error', () => {
        status.textContent = 'This chart could not load. Please return to all charts and try again.';
      });
      back.addEventListener('click', () => {
        showDirectory();
        selectedLink?.focus({ preventScroll: true });
      });
      zoom.addEventListener('click', () => {
        const zoomed = stage.classList.toggle('is-zoomed');
        zoom.textContent = zoomed ? 'Fit image' : 'Zoom in';
        zoom.setAttribute('aria-pressed', String(zoomed));
      });
    });
  };

  const validateConfiguredCart = (cart) => {
    if (visibleCartItems(cart).some((item) => item.sku === 'BOWBAR-PRICING-BASE' && !item.properties?._bowbar_configuration)) return false;
    const parents = visibleCartItems(cart).filter((item) => item.properties?._bowbar_configuration);
    for (const parent of parents) {
      const parts = cart.items.filter((item) => isPricingFragment(item) && item.properties?._bowbar_configuration === parent.properties._bowbar_configuration);
      const expected = Number(parent.properties._bowbar_unit_cents);
      if (!Number.isSafeInteger(expected) || expected <= 0 || parts.length !== Number(parent.properties._bowbar_fragment_count) || parts.some((part) => part.quantity !== parent.quantity) || parts.reduce((sum, part) => sum + Number(part.original_line_price ?? part.line_price), 0) !== expected * parent.quantity) return false;
    }
    return !cart.items.some((part) => isPricingFragment(part) && !parents.some((parent) => parent.properties._bowbar_configuration === part.properties?._bowbar_configuration));
  };

  document.addEventListener('click', async (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-bowbar-page-change],[data-bowbar-page-remove]') : null;
    if (!button) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (cartState.busy) return;
    updateCartLine(button.dataset.bowbarCartKey, button.hasAttribute('data-bowbar-page-remove') ? 0 : Number(button.dataset.bowbarPageQuantity) + Number(button.dataset.bowbarPageChange));
  }, true);

  // Validate linked merchandise before the theme's checkout entry points. Discounts
  // may change final totals, so the charge check deliberately uses original prices.
  let checkoutApproved = false;
  const checkoutGuard = async (event) => {
    if (checkoutApproved) return;
    const element = event.target instanceof Element ? event.target : null;
    const checkout = event.type === 'submit' ? (event.submitter?.name === 'checkout' ? event.submitter : null) : element?.closest('a[href="/checkout"],button[name="checkout"],input[name="checkout"]');
    if (!checkout) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    try {
      const cart = await fetchCart();
      if (!validateConfiguredCart(cart)) throw new Error('Please remove the incomplete custom item and add it again so its price can be confirmed.');
      checkoutApproved = true;
      window.location.assign(`${shopRoot()}checkout`);
    } catch (error) {
      let message = document.querySelector('[data-bowbar-checkout-error]');
      if (!message) {
        message = createCartNode('p', 'bowbar-cart-drawer__error');
        message.dataset.bowbarCheckoutError = 'true';
        message.setAttribute('role', 'alert');
        checkout.parentElement.prepend(message);
      }
      message.textContent = error.message || 'Unable to confirm your cart. Please try again.';
    }
  };
  document.addEventListener('click', checkoutGuard, true);
  document.addEventListener('submit', checkoutGuard, true);

  const init = (scope = document) => {
    const quoteDetails = scope.querySelector('[data-bowbar-quote-details]');
    const requestedDesign = new URL(window.location.href).searchParams.get('design');
    if (quoteDetails && !quoteDetails.value && requestedDesign) quoteDetails.value = `Design request: ${requestedDesign.slice(0,160)}\n\n`;
    initHeader(scope);
    initCartDrawer(scope);
    initReveal(scope);
    initSliders(scope);
    initNewArrivalsSplide(scope);
    initAjaxProductForms(scope);
    initProduct(scope);
    initChartGuides(scope);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init()); else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
